import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { IconArrowRotateLeftRight } from 'central-icons/IconArrowRotateLeftRight';
import classNames from 'classnames';
import { formatDistanceToNow, intlFormat } from 'date-fns';
import {
  isValidElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrevious } from 'react-use';

import { isRoleRevelioAdmin, useGetLoggedInUser } from '@revelio/auth';
import { DeliveryStatus } from '@revelio/data-access';
import { TableChart } from '@revelio/replots';
import { IconButton, Spinner, Text } from '@revelio/ui';

import { useEffectiveUserId } from '../../adminRewrite/impersonation/useEffectiveUserId';
import type { ReportRun } from '../../CustomDash/components/Reports/types';
import { useReportRunsQuery } from '../../CustomDash/hooks/useReportRunQuery';
import {
  type UserOption,
  UserSelector,
} from '../../DataBuilder/components/user-selector';
import { useAllReports } from '../hooks/use-all-reports';
import { ReportsStatusColumn } from './reports-status-column';
import styles from './reports-table.module.css';
import { useReportTableData } from './use-report-table-data';

type ReportRow = ReturnType<typeof useReportTableData>['data'][number];

const DEFAULT_SORTING: SortingState = [{ id: 'created_at', desc: true }];

export const ReportsTable = ({
  onInitialized,
}: {
  onInitialized?: () => void;
}) => {
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [timeDistance, setTimeDistance] = useState<string>('');
  const minsPast = useRef<number>(0);
  const [selectedUserOption, setSelectedUserOption] = useState<UserOption>();
  const { loggedInUser } = useGetLoggedInUser();
  const effectiveUserId = useEffectiveUserId();
  const { data, fetching, refresh } = useReportTableData(selectedUserOption);
  const { reports, executeQuery } = useAllReports({
    userId: selectedUserOption?.value,
  });
  const { data: reportRuns = [] } = useReportRunsQuery(
    selectedUserOption?.value || '',
    !!selectedUserOption?.value
  );
  const previousFetch = usePrevious(fetching);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  useEffect(() => {
    if (hasInitialized && onInitialized) {
      onInitialized();
    }
  }, [hasInitialized, onInitialized]);

  useEffect(() => {
    if (previousFetch === true && fetching === false) {
      setHasInitialized(true);
    }
  }, [previousFetch, fetching]);

  /** Re-sync the picker when the effective user changes (impersonation applied or
   * lifted); a manual selection persists until then. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: loggedInUser supplies display-only label fields; the picker must re-sync only when the effective user changes
  useEffect(() => {
    if (effectiveUserId) {
      setSelectedUserOption({
        value: effectiveUserId,
        label: loggedInUser.name || '',
        group: loggedInUser.client_name || '',
        secondaryLabel: loggedInUser.email || '',
      });
    }
  }, [effectiveUserId]);

  const showUserSelector = isRoleRevelioAdmin(loggedInUser.role);

  const refreshNow = useCallback(() => {
    setLastUpdate(new Date());
    refresh();
    minsPast.current = 0;
  }, [refresh]);

  const intChecker = useCallback(() => {
    minsPast.current++;
    if (minsPast.current >= 4) {
      refreshNow();
    }
  }, [refreshNow]);

  useEffect(() => {
    setTimeDistance(formatDistanceToNow(lastUpdate, { addSuffix: true }));

    const intRefresh = setInterval(() => {
      setTimeDistance(formatDistanceToNow(lastUpdate, { addSuffix: true }));
      intChecker();
    }, 60000);

    return () => clearInterval(intRefresh);
  }, [intChecker, lastUpdate]);

  const secondaryColumn: ColumnDef<ReportRow, unknown> =
    !selectedUserOption?.isGroupOption
      ? { accessorKey: 'client', header: 'Client' }
      : { accessorKey: 'user', header: 'User' };

  const columns = useMemo<ColumnDef<ReportRow, unknown>[]>(
    () => [
      { accessorKey: 'name', header: 'Name' },
      {
        accessorKey: 'reportType',
        header: 'Report Type',
        cell: (info) => <div>{String(info.getValue() ?? '')}</div>,
      },
      secondaryColumn,
      {
        accessorKey: 'created_at',
        header: 'Creation',
        cell: (info) => (
          <div>
            {intlFormat(new Date(String(info.getValue())), {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              timeZoneName: 'short',
            })}
          </div>
        ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated',
        cell: (info) => (
          <div>
            {formatDistanceToNow(new Date(String(info.getValue())), {
              addSuffix: true,
            })}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ getValue, row }) => {
          const tableData = row.original;
          return (
            <ReportsStatusColumn
              viewReportLink={tableData.viewReportLink}
              value={String(getValue())}
              report={reports.find((r) => r && r.id === tableData.id) || null}
              customReportRun={
                reportRuns.find((r: ReportRun) => r && r.id === tableData.id) ||
                null
              }
              reportStatus={tableData.status ?? DeliveryStatus.Undefined}
              refreshReports={executeQuery}
            />
          );
        },
      },
    ],
    [secondaryColumn, reports, reportRuns, executeQuery]
  );

  // Make rows navigable (Finished rows only), mirroring the screener's pattern.
  const onRenderRow = useCallback(
    ({
      rowContent,
      rowData,
    }: {
      rowContent: ReactNode;
      rowData: ReportRow;
    }) => {
      if (!isValidElement(rowContent)) {
        return rowContent;
      }
      const clickable = rowData.status === DeliveryStatus.Finished;
      const link = rowData.viewReportLink;
      const originalClassName = rowContent.props?.className || '';
      const originalStyle = rowContent.props?.style || {};
      return (
        <tr
          className={originalClassName}
          style={{
            ...originalStyle,
            cursor: clickable ? 'pointer' : 'default',
          }}
          onClick={() => {
            if (clickable && link) navigate(link);
          }}
        >
          {rowContent.props?.children}
        </tr>
      );
    },
    [navigate]
  );

  const hasData = data.length > 0;

  let tableContent: ReactNode;
  if (!hasData && fetching) {
    tableContent = (
      <div className={styles.placeholderState}>
        <Spinner className={styles.loadingSpinner} />
      </div>
    );
  } else if (hasData) {
    tableContent = (
      <TableChart
        data={data}
        columns={columns}
        showZebraStripe={false}
        onRenderRow={onRenderRow}
        controlledSorting={{
          externalSortingState: sorting,
          onSortingChange: setSorting,
        }}
      />
    );
  } else {
    tableContent = (
      <div className={classNames(styles.emptyText, styles.placeholderState)}>
        <Text>No reports created yet</Text>
      </div>
    );
  }

  return (
    <>
      <div className={styles.toolbar}>
        {showUserSelector && (
          <UserSelector
            selectedUserOption={selectedUserOption}
            setSelectedUserOption={setSelectedUserOption}
          />
        )}
      </div>
      <div
        className={styles.gridContainer}
        data-with-toolbar={showUserSelector}
      >
        <div className={classNames(styles.tableWrap, styles.tableArea)}>
          {/* Refresh control, top-right over the table (replaces the old column
              menu slot). */}
          <IconButton
            aria-label="Refresh Status"
            icon={<IconArrowRotateLeftRight size={14} />}
            className={styles.refreshButton}
            variant="outlined"
            size="sm"
            shape="squared"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              refreshNow();
            }}
          />
          {tableContent}
        </div>
      </div>
      {lastUpdate && (
        <div className={styles.lastUpdatedRow}>
          <Text className={styles.lastUpdated} fontSize="x-sm">
            <IconArrowRotateLeftRight
              size="1em"
              className={styles.lastUpdatedIcon}
            />
            <span>Last updated: {timeDistance}</span>
          </Text>
        </div>
      )}
    </>
  );
};
